import { firebase } from "common/src/config/configureFirebase";

class SubscriptionAPI {
    static getBaseUrl() {
        // Get project ID dynamically from Firebase config
        let projectId =
            firebase?.config?.projectId || firebase?.app?.options?.projectId;

        // Fallback: try to get from environment variables or other sources
        if (!projectId) {
            // Check if running in web environment and try to get from window object
            if (typeof window !== "undefined" && window.firebaseConfig) {
                projectId = window.firebaseConfig.projectId;
            }
            // Add other fallback methods as needed
        }

        if (!projectId) {
            console.error(
                "Firebase project ID not found. Make sure Firebase is properly initialized."
            );
            // Fallback to default project ID with warning
            console.warn(
                "Using fallback project ID. Please ensure Firebase is properly configured."
            );
            projectId = "quaumovil"; // fallback
        }

        // Always use cloud functions URL for subscription APIs
        return `https://us-central1-${projectId}.cloudfunctions.net`;
    }

    static async initializeSubscriptionPlans(language = "en") {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-initSubscriptionPlans`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ language }),
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error initializing plans:", error);
            return {
                success: false,
                error: "Backend initialization function is disabled for safety",
            };
        }
    }

    static async getSubscriptionPlans(userType = null, language = "en") {
        try {
            let url = `${this.getBaseUrl()}/subscription-getSubscriptionPlans?language=${language}&showAll=true`;
            if (userType) {
                url += `&userType=${userType}`;
            }

            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            return await response.json();
        } catch (error) {
            console.error("Error fetching plans:", error);
            return { success: false, error: error.message };
        }
    }

    static async createSubscriptionPlan(planData) {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-createSubscriptionPlan`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ planData }),
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error creating plan:", error);
            return { success: false, error: error.message };
        }
    }

    static async updateSubscriptionPlan(planId, updates) {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-updateSubscriptionPlan`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ planId, updates }),
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error updating plan:", error);
            return { success: false, error: error.message };
        }
    }

    static async deleteSubscriptionPlan(planId, transferToPlanId = null) {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-deleteSubscriptionPlan`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ planId, transferToPlanId }),
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error deleting plan:", error);
            return { success: false, error: error.message };
        }
    }

    static async createSubscription(subscriptionData) {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-createSubscription`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(subscriptionData),
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error creating subscription:", error);
            return { success: false, error: error.message };
        }
    }

    static async getUserSubscription(userId) {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-getUserSubscription?userId=${userId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error fetching user subscription:", error);
            return { success: false, error: error.message };
        }
    }

    static async getSubscriptionStats() {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-getSubscriptionStats`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            return await response.json();
        } catch (error) {
            console.error("Error fetching subscription stats:", error);
            return { success: false, error: error.message };
        }
    }

    static async getSubscriptions() {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-getSubscriptions`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log("🔍 API Response:", response);

            return await response.json();
        } catch (error) {
            console.error("Error fetching subscriptions:", error);
            return { success: false, error: error.message };
        }
    }

    static async checkTripUsage(userId, action = "check") {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-checkTripUsage`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId,
                        action,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error checking trip usage:", error);
            throw error;
        }
    }

    static async verifyOfflinePayment(userId, approved, adminNote = "") {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-verifyOfflinePayment`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId,
                        approved,
                        adminNote,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error verifying offline payment:", error);
            throw error;
        }
    }

    static async getSubscriptionTransactions(filters = {}) {
        try {
            const params = new URLSearchParams();

            Object.keys(filters).forEach((key) => {
                if (
                    filters[key] &&
                    filters[key] !== "all" &&
                    filters[key] !== ""
                ) {
                    params.append(key, filters[key]);
                }
            });

            console.log("🔍 API Request filters:", filters);
            console.log("🔍 API Request params:", params.toString());

            const response = await fetch(
                `${this.getBaseUrl()}/subscription-getSubscriptionTransactions?${params.toString()}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log("🔍 API Response:", result);
            return result;
        } catch (error) {
            console.error("Error fetching subscription transactions:", error);
            throw error;
        }
    }

    static async updateSubscriptionStatus(userId, status, adminNote = "") {
        try {
            console.log("📡 API Request:", {
                url: `${this.getBaseUrl()}/subscription-updateSubscriptionStatus`,
                body: { userId, status, adminNote },
            });

            const response = await fetch(
                `${this.getBaseUrl()}/subscription-updateSubscriptionStatus`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId,
                        status,
                        adminNote,
                    }),
                }
            );

            console.log("📡 API Response status:", response.status);

            if (!response.ok) {
                // Try to get error details from response body
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorMessage;
                    console.error("📡 API Error details:", errorData);
                } catch (parseError) {
                    console.error(
                        "📡 Could not parse error response:",
                        parseError
                    );
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log("📡 API Success response:", result);
            return result;
        } catch (error) {
            console.error("Error updating subscription status:", error);
            throw error;
        }
    }

    static async changeSubscriptionPlan(userId, newPlanId, adminNote = "") {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-changeSubscriptionPlan`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId,
                        newPlanId,
                        adminNote,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error changing subscription plan:", error);
            throw error;
        }
    }

    static async cancelSubscription(
        userId,
        cancelReason = "",
        cancelledBy = "admin"
    ) {
        try {
            const response = await fetch(
                `${this.getBaseUrl()}/subscription-cancelSubscription`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userId,
                        cancelReason,
                        cancelledBy,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error cancelling subscription:", error);
            throw error;
        }
    }

    static getCurrentLanguage() {
        try {
            const savedLang = localStorage.getItem("language");
            if (savedLang) return savedLang;

            const browserLang = navigator.language?.split("-")[0];
            return ["en", "es"].includes(browserLang) ? browserLang : "en";
        } catch (error) {
            console.warn("Could not get language, defaulting to English");
            return "en";
        }
    }

    static formatPlanForDisplay(plan, language = "en") {
        if (!plan) return null;

        if (plan.name && plan.description && plan.features) {
            return {
                ...plan,
                formattedPrice: new Intl.NumberFormat(language, {
                    style: "currency",
                    currency: "USD",
                }).format(plan.price),
                formattedPeriod: this.formatPeriod(plan.period, language),
            };
        }

        if (plan.translations) {
            const translation =
                plan.translations[language] || plan.translations["en"];
            return {
                ...plan,
                name: translation?.name || plan.id,
                description: translation?.description || "",
                features: translation?.features || [],
                formattedPrice: new Intl.NumberFormat(language, {
                    style: "currency",
                    currency: "USD",
                }).format(plan.price),
                formattedPeriod: this.formatPeriod(plan.period, language),
            };
        }

        return plan;
    }

    static formatPeriod(period, language = "en") {
        const periods = {
            en: {
                daily: "Daily",
                weekly: "Weekly",
                monthly: "Monthly",
                yearly: "Yearly",
            },
            es: {
                daily: "Diario",
                weekly: "Semanal",
                monthly: "Mensual",
                yearly: "Anual",
            },
        };

        return periods[language]?.[period] || periods["en"][period] || period;
    }

    static formatPaymentMethods(paymentMethods, language = "en") {
        const methodNames = {
            en: {
                stripe: "Credit Card (Stripe)",
                paypal: "PayPal",
                yappy: "Yappy Payment",
                cash: "Cash Payment",
            },
            es: {
                stripe: "Tarjeta de Crédito (Stripe)",
                paypal: "PayPal",
                yappy: "Pago Yappy",
                cash: "Pago en Efectivo",
            },
        };

        return paymentMethods.map(
            (method) =>
                methodNames[language]?.[method] ||
                methodNames["en"][method] ||
                method
        );
    }
}

export default SubscriptionAPI;
